import { useCallback, useEffect, useRef, useState } from "react";
import config from "@/config";
import type {
  PatientDetailResponse,
  PatientGroup,
  PatientsResponse,
} from "@/types/intake";

const DEFAULT_TZ = "America/New_York";

export interface UsePatientsParams {
  lookback?: number;
  seenMin?: number;
  seenTodayOnly?: boolean;
  tz?: string;
  pageSize?: number;
}

/**
 * Paginated GET /api/dashboard/patients. Filters (lookback, seenMin,
 * seenTodayOnly) are applied server-side to the rolled-up registry; pagination
 * happens after that, so changing a filter resets to page 0.
 */
export function usePatients({
  lookback = 14,
  seenMin = 1,
  seenTodayOnly = false,
  tz = DEFAULT_TZ,
  pageSize = 50,
}: UsePatientsParams) {
  const [patients, setPatients] = useState<PatientGroup[]>([]);
  const [summary, setSummary] = useState<PatientsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const seqRef = useRef(0);

  const fetchPage = useCallback(
    async (append: boolean) => {
      const seq = ++seqRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setPatients([]);
        offsetRef.current = 0;
      }
      setError(null);
      try {
        const params = new URLSearchParams({
          lookback: String(lookback),
          seen_min: String(seenMin),
          seen_today_only: seenTodayOnly ? "true" : "false",
          tz,
          limit: String(pageSize),
          offset: String(append ? offsetRef.current : 0),
        });
        const res = await fetch(
          `${config.API_BASE_URL}/api/dashboard/patients?${params.toString()}`,
        );
        if (!res.ok) throw new Error(`patients failed: ${res.status}`);
        const next = (await res.json()) as PatientsResponse;
        if (seq !== seqRef.current) return;
        setSummary(next);
        setPatients((prev) =>
          append ? [...prev, ...next.patients] : next.patients,
        );
        setHasMore(next.has_more);
        offsetRef.current = (append ? offsetRef.current : 0) + next.returned_count;
      } catch (err) {
        if (seq !== seqRef.current) return;
        setError(err instanceof Error ? err.message : "Failed to load patients");
      } finally {
        if (seq === seqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [lookback, seenMin, seenTodayOnly, tz, pageSize],
  );

  useEffect(() => {
    void fetchPage(false);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) void fetchPage(true);
  }, [fetchPage, loadingMore, hasMore]);

  const refresh = useCallback(() => {
    void fetchPage(false);
  }, [fetchPage]);

  return {
    patients,
    summary,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}

export function usePatientDetail(
  patientKey: string | null,
  lookback: number = 180,
  tz: string = DEFAULT_TZ,
) {
  const [data, setData] = useState<PatientDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const fetchDetail = useCallback(async () => {
    if (!patientKey) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const seq = ++seqRef.current;
    setData(null);
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        lookback: String(lookback),
        tz,
      });
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/patients/${encodeURIComponent(patientKey)}?${params.toString()}`,
      );
      if (!res.ok) throw new Error(`patient detail failed: ${res.status}`);
      const next = (await res.json()) as PatientDetailResponse;
      if (seq !== seqRef.current) return;
      setData(next);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(
        err instanceof Error ? err.message : "Failed to load patient detail",
      );
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [patientKey, lookback, tz]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  return { data, loading, error, refresh: fetchDetail };
}
